import * as React from 'react'

import { useF0FormDefinition } from '@factorialco/f0-react'
import type { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { PayrollConceptWithCategory } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'

import {
  type AdditionalCompensationWizardInitialValues,
  type BasicContext,
  type BasicStepValues,
  type DefinitionStepValues,
  FIXED_AMOUNT_STRATEGY,
  type RawDefinitionStepValues,
} from './lib/additionalCompensationWizard.shared'
import { buildBasicSchema } from './lib/additionalCompensationWizardBasicSchema'
import { buildDefinitionSchema } from './lib/additionalCompensationWizardDefinitionSchema'
import {
  buildAmountStrategyTypeOptions,
  buildRecurrenceOptions,
  getDefinitionSectionCopy,
} from './lib/additionalCompensationWizard.utils'

type UseAdditionalCompensationWizardFormParams = {
  formName: string
  submitLabel: string
  initialValues?: AdditionalCompensationWizardInitialValues
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  payrollConceptOptions: PayrollConceptWithCategory[]
  effectiveOn?: string
  defaultFirstPaymentDate?: Date
  basicContext: BasicContext
  selectedPayrollConceptUnitType?: PayrollConceptSelectFieldFragment['unitType']
  onSubmit: ({
    sectionId,
    data,
  }: {
    sectionId: 'basic' | 'definition'
    data: BasicStepValues | RawDefinitionStepValues
  }) => Promise<{ success: boolean }>
}

export const useAdditionalCompensationWizardForm = ({
  formName,
  submitLabel,
  initialValues,
  payrollConcepts,
  payrollConceptOptions,
  effectiveOn,
  defaultFirstPaymentDate,
  basicContext,
  selectedPayrollConceptUnitType,
  onSubmit,
}: UseAdditionalCompensationWizardFormParams) => {
  const locale = i18n.locale

  const definitionSectionCopy = React.useMemo(
    () => getDefinitionSectionCopy(basicContext.amountStrategyType),
    [basicContext.amountStrategyType, locale]
  )

  const recurrenceOptions = React.useMemo(() => buildRecurrenceOptions(), [locale])

  const amountStrategyTypeOptions = React.useMemo(() => buildAmountStrategyTypeOptions(), [locale])

  const basicSchema = React.useMemo(
    () =>
      buildBasicSchema({
        payrollConceptOptions,
        amountStrategyTypeOptions,
        recurrenceOptions,
        effectiveOn,
        defaultFirstPaymentDate,
      }),
    [
      amountStrategyTypeOptions,
      defaultFirstPaymentDate,
      effectiveOn,
      locale,
      payrollConceptOptions,
      recurrenceOptions,
    ]
  )

  const definitionSchema = React.useMemo(
    () =>
      buildDefinitionSchema({
        payrollConcepts,
        basicContext,
        selectedPayrollConceptUnitType,
      }),
    [basicContext, locale, payrollConcepts, selectedPayrollConceptUnitType]
  )

  const defaultValues = React.useMemo<AdditionalCompensationWizardInitialValues>(() => {
    const basicDefaultValues: Partial<BasicStepValues> = {
      amountStrategyType: FIXED_AMOUNT_STRATEGY,
      recurrence: 'monthly',
      ...(defaultFirstPaymentDate ? { firstPaymentOn: defaultFirstPaymentDate } : {}),
      ...initialValues?.basic,
    }

    const definitionDefaultValues: Partial<DefinitionStepValues> | undefined =
      initialValues?.definition

    return {
      basic: basicDefaultValues,
      ...(definitionDefaultValues ? { definition: definitionDefaultValues } : {}),
    }
  }, [defaultFirstPaymentDate, initialValues])

  const formDefinition = useF0FormDefinition({
    name: formName,
    schema: {
      basic: basicSchema,
      definition: definitionSchema,
    },
    sections: {
      basic: {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.sections.compensation_kind.title'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.sections.compensation_kind.description'
        ),
      },
      definition: {
        title: definitionSectionCopy.title,
        ...(definitionSectionCopy.description
          ? { description: definitionSectionCopy.description }
          : {}),
        withInset: false,
      },
    },
    defaultValues,
    submitConfig: {
      label: submitLabel,
    },
    onSubmit,
    errorTriggerMode: 'on-submit',
  })

  const steps = React.useMemo(
    () => [
      {
        title: i18n.t('common.basic_information'),
        sectionIds: ['basic'],
      },
      {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.title'
        ),
        sectionIds: ['definition'],
      },
    ],
    [locale]
  )

  return { formDefinition, steps }
}
